import { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Calendar, Clock, MapPin, Save, Loader2 } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

interface EventEditDialogProps {
  event: {
    id: string;
    name: string;
    description?: string;
    rsvp_date?: string;
    rsvp_time?: string;
    rsvp_location?: string;
  };
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSave?: () => void;
}

export const EventEditDialog = ({ event, open, onOpenChange, onSave }: EventEditDialogProps) => {
  const [formData, setFormData] = useState({
    name: event.name || '',
    description: event.description || '',
    rsvp_date: event.rsvp_date || '',
    rsvp_time: event.rsvp_time || '',
    rsvp_location: event.rsvp_location || ''
  });
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    if (!formData.name.trim()) {
      toast.error('Event name is required');
      return;
    }

    try {
      setSaving(true);

      const { error } = await supabase
        .from('events')
        .update({
          name: formData.name.trim(),
          description: formData.description.trim() || null,
          rsvp_date: formData.rsvp_date || null,
          rsvp_time: formData.rsvp_time || null,
          rsvp_location: formData.rsvp_location.trim() || null,
          updated_at: new Date().toISOString()
        })
        .eq('id', event.id);

      if (error) throw error;

      toast.success('Event updated successfully');
      onOpenChange(false);
      onSave?.();
    } catch (error) {
      console.error('Error updating event:', error);
      toast.error('Failed to update event');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>Edit Event</DialogTitle>
        </DialogHeader>

        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <Label htmlFor="event-name">Event Name</Label>
            <Input
              id="event-name"
              value={formData.name}
              onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
              placeholder="Enter event name"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="event-description">Description</Label>
            <Textarea
              id="event-description"
              value={formData.description}
              onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
              placeholder="Enter event description"
              rows={3}
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="event-date" className="flex items-center gap-2">
                <Calendar className="h-4 w-4" />
                Date
              </Label>
              <Input
                id="event-date"
                type="date"
                value={formData.rsvp_date}
                onChange={(e) => setFormData(prev => ({ ...prev, rsvp_date: e.target.value }))}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="event-time" className="flex items-center gap-2">
                <Clock className="h-4 w-4" />
                Time
              </Label>
              <Input
                id="event-time"
                type="time"
                value={formData.rsvp_time}
                onChange={(e) => setFormData(prev => ({ ...prev, rsvp_time: e.target.value }))}
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="event-location" className="flex items-center gap-2">
              <MapPin className="h-4 w-4" />
              Location
            </Label>
            <Input
              id="event-location"
              value={formData.rsvp_location}
              onChange={(e) => setFormData(prev => ({ ...prev, rsvp_location: e.target.value }))}
              placeholder="Enter event location"
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={handleSave} disabled={saving}>
            {saving ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Saving...
              </>
            ) : (
              <>
                <Save className="h-4 w-4 mr-2" />
                Save Changes
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default EventEditDialog;
